// @ts-strict-ignore
import type { ForwardedRef, MutableRefObject } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import type { KeyboardFocus } from '@usealloy/component-library';
import { useUniqueId } from '@usealloy/component-library';

// Helper to set either a callback ref or a RefObject<T>
function assignRef<T>(ref: ForwardedRef<T>, value: T | null) {
  if (typeof ref === 'function') {
    ref(value);
  } else if (ref) {
    (ref as MutableRefObject<T | null>).current = value;
  }
}

/**
 * Keyboard-focus registration for MultiSelect wrappers. Returns a stable
 * callback ref (`setInstance`) to pass as `ref`, plus `onFocus` and `id`.
 *
 * Nullable `keyboardFocus` safely no-ops for callers outside a provider
 * (e.g. data-route aggregations).
 */
export function useMultiSelectKeyboardFocus({
  keyboardFocus,
  idPrefix = 'multiselect',
  id: propsId,
  forwardedRef,
}: {
  keyboardFocus: KeyboardFocus | null;
  idPrefix?: string;
  id?: string;
  forwardedRef?: ForwardedRef<unknown>;
}) {
  const uuid = useUniqueId(idPrefix);
  const id = propsId || uuid;

  /*
   * We cannot attach a ref directly to the focusable html element of the multiselect
   * so we have to do some ref sorcery to get it. React 18's strict mode also
   * sometimes leads to unintended unregister calls with how it handles dependency
   * changes & callbacks. We add additional stabilization measures to handle that.
   */

  // Keep latest keyboardFocus instance in a ref to stabilize callback
  const keyboardFocusRef = useRef(keyboardFocus);

  useEffect(() => {
    keyboardFocusRef.current = keyboardFocus;
  }, [keyboardFocus]);

  // This ref will point to the focusable element (or a ref object your hook expects)
  const elementRef = useRef<HTMLElement>(null);

  // Stable callback ref: ignore null, register only when we get a real node
  const setInstance = useCallback((inst: any | null) => {
    // If MultiSelect exposes inputRef as a ref object, grab .current.
    const nextNode: HTMLElement = inst?.inputRef?.current ?? inst?.inputRef ?? null;

    if (!nextNode) return;

    if (elementRef.current !== nextNode) {
      elementRef.current = nextNode;
      assignRef(forwardedRef, nextNode);
      keyboardFocusRef.current?.register({ id, ref: elementRef });
    }
  }, [id, forwardedRef]);

  useEffect(() => () => keyboardFocusRef.current?.unregister(id), [id]);

  // A workaround for the lack of exposed onClick handling in Multiselect.
  // Leads to redundant setFocusedElement calls when an item is focused via a click.
  const onFocus = () => keyboardFocus?.setFocusedElement(id);

  return { id, setInstance, onFocus };
}
